/**
 * Storage Manager
 * Handles switching between storage adapters and data migration
 */

import { storageService } from './storage.service';
import SQLiteAdapter from './adapters/sqlite.adapter';

// Storage types
export type StorageType = 'localStorage' | 'sqlite';

// LocalStorage adapter (copy from storage.service.ts)
class LocalStorageAdapter {
  async getItem(key: string): Promise<string | null> {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('LocalStorage getItem error:', error);
      return null;
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error('LocalStorage setItem error:', error);
      throw error;
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('LocalStorage removeItem error:', error);
      throw error;
    }
  }

  async clear(): Promise<void> {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('LocalStorage clear error:', error);
      throw error;
    }
  }

  async getAllKeys(): Promise<string[]> {
    try {
      return Object.keys(localStorage);
    } catch (error) {
      console.error('LocalStorage getAllKeys error:', error);
      return [];
    }
  }
}

class StorageManager {
  private static instance: StorageManager;
  private currentStorageType: StorageType = 'localStorage';
  private storageTypeKey = 'fishAppStorageType';

  private constructor() {
    // Default to SQLite for all platforms
    // - Web: Uses IndexedDB (via SQLite adapter fallback)
    // - Native: Uses native SQLite (@capacitor-community/sqlite)
    const defaultStorageType: StorageType = 'sqlite';
    
    // Load current storage type from localStorage (bootstrap)
    try {
      const savedType = localStorage.getItem(this.storageTypeKey) as StorageType;
      if (savedType === 'sqlite' || savedType === 'localStorage') {
        this.currentStorageType = savedType;
      } else {
        // No saved preference, use default (sqlite/IndexedDB)
        this.currentStorageType = defaultStorageType;
        // Save the default for next time
        localStorage.setItem(this.storageTypeKey, defaultStorageType);
        console.log(`📦 Storage initialized with default: ${defaultStorageType}`);
      }
    } catch (error) {
      console.error('Failed to load storage type:', error);
      // Fallback to default
      this.currentStorageType = defaultStorageType;
    }
  }

  static getInstance(): StorageManager {
    if (!StorageManager.instance) {
      StorageManager.instance = new StorageManager();
    }
    return StorageManager.instance;
  }

  getCurrentStorageType(): StorageType {
    return this.currentStorageType;
  }

  async switchStorageType(newType: StorageType): Promise<{ success: boolean; error?: string }> {
    if (newType === this.currentStorageType) {
      return { success: true };
    }

    console.log(`🔄 Starting storage migration: ${this.currentStorageType} → ${newType}`);
    const originalType = this.currentStorageType;
    let migrationSuccessful = false;

    try {
      // Step 1: Get current adapter and validate it's working
      const currentAdapter = this.createAdapter(this.currentStorageType);
      console.log('✓ Current adapter created');
      
      // Step 2: Get all data from current storage
      const allKeys = await this.getAllKeys(currentAdapter);
      console.log(`✓ Found ${allKeys.length} keys to migrate`);

      if (allKeys.length === 0) {
        console.warn('⚠️ No data found in current storage - proceeding anyway');
      }

      const dataToMigrate: Array<{ key: string; value: string }> = [];
      let failedReads = 0;
      
      for (const key of allKeys) {
        try {
          const value = await currentAdapter.getItem(key);
          if (value !== null) {
            dataToMigrate.push({ key, value });
          }
        } catch (readError) {
          console.error(`Failed to read key "${key}":`, readError);
          failedReads++;
        }
      }

      if (failedReads > 0) {
        console.warn(`⚠️ Failed to read ${failedReads} keys, continuing with ${dataToMigrate.length} items`);
      }

      console.log(`✓ Read ${dataToMigrate.length} items successfully`);

      // Step 3: Create new adapter and ensure it initializes
      console.log('Creating new adapter...');
      const newAdapter = this.createAdapter(newType);
      
      // Give adapter time to initialize (especially important for SQLite)
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('✓ New adapter created and initialized');

      // Step 4: Migrate all data to new storage with error tracking
      let successfulWrites = 0;
      let failedWrites = 0;
      
      for (const { key, value } of dataToMigrate) {
        try {
          await newAdapter.setItem(key, value);
          successfulWrites++;
        } catch (writeError) {
          console.error(`Failed to write key "${key}":`, writeError);
          failedWrites++;
          
          // If too many writes fail, abort migration
          if (failedWrites > Math.max(1, dataToMigrate.length * 0.1)) {
            throw new Error(`Too many write failures (${failedWrites}), aborting migration`);
          }
        }
      }

      console.log(`✓ Migrated ${successfulWrites}/${dataToMigrate.length} items successfully`);

      // Step 5: Verify migration by spot-checking a few random keys
      if (dataToMigrate.length > 0) {
        console.log('Verifying migration...');
        const samplesToCheck = Math.min(5, dataToMigrate.length);
        const indices = Array.from({length: samplesToCheck}, () => 
          Math.floor(Math.random() * dataToMigrate.length)
        );
        
        let verificationFailed = false;
        for (const idx of indices) {
          const { key, value } = dataToMigrate[idx];
          const readValue = await newAdapter.getItem(key);
          if (readValue !== value) {
            console.error(`Verification failed for key "${key}"`);
            verificationFailed = true;
          }
        }
        
        if (verificationFailed) {
          throw new Error('Migration verification failed - data integrity issue');
        }
        
        console.log('✓ Migration verification passed');
      }

      // Step 6: Update storage type preference
      this.currentStorageType = newType;
      
      // Save to both storages to ensure persistence
      try {
        localStorage.setItem(this.storageTypeKey, newType);
        console.log('✓ Storage type saved to localStorage');
      } catch (e) {
        console.warn('Failed to save storage type to localStorage:', e);
      }

      await newAdapter.setItem(this.storageTypeKey, newType);
      console.log('✓ Storage type saved to new adapter');

      // Step 7: Update the global storage service to use new adapter
      await this.reinitializeStorageService(newAdapter);
      console.log('✓ Storage service reinitialized');

      // Step 8: Optional - clear old storage after successful migration
      // Keeping data in old storage as backup for now
      // await currentAdapter.clear();

      migrationSuccessful = true;
      console.log(`✅ Storage migration completed successfully: ${originalType} → ${newType}`);
      
      return { success: true };
    } catch (error: any) {
      console.error('❌ Storage migration failed:', error);
      
      // Rollback: restore original storage type
      if (!migrationSuccessful) {
        console.log(`↩️ Rolling back to ${originalType}`);
        this.currentStorageType = originalType;
        
        try {
          const originalAdapter = this.createAdapter(originalType);
          await this.reinitializeStorageService(originalAdapter);
          console.log('✓ Rollback completed');
        } catch (rollbackError) {
          console.error('Failed to rollback:', rollbackError);
        }
      }
      
      return { 
        success: false, 
        error: error.message || 'Unknown error during migration' 
      };
    }
  }

  private createAdapter(type: StorageType): any {
    if (type === 'sqlite') {
      return new SQLiteAdapter();
    } else {
      return new LocalStorageAdapter();
    }
  }

  private async getAllKeys(adapter: any): Promise<string[]> {
    // Try to use getAllKeys method if available
    if (typeof adapter.getAllKeys === 'function') {
      return await adapter.getAllKeys();
    }

    // Fallback: Assume localStorage-like behavior
    if (adapter instanceof LocalStorageAdapter) {
      return Object.keys(localStorage);
    }

    return [];
  }

  private async reinitializeStorageService(newAdapter: any): Promise<void> {
    // Update the adapter in the singleton storage service
    storageService.setAdapter(newAdapter);
    console.log('Storage service adapter updated');
  }

  // Get the appropriate adapter for current storage type
  getAdapter(): any {
    return this.createAdapter(this.currentStorageType);
  }
}

export const storageManager = StorageManager.getInstance();
export default StorageManager;
